import { statusDefinitions } from "./status";
import { getDescendantIds, isLeaf } from "./tree";
import type { WorkItem, WorkStatus } from "../types/domain";

export interface ProgressResult {
  percent: number;
  weightedCompleted: number;
  weightedTotal: number;
  consideredItems: number;
}

export function calculateWeightedProgress(
  items: readonly WorkItem[],
  scopeRootId?: string,
): ProgressResult {
  const scopeIds = scopeRootId ? getDescendantIds(items, scopeRootId) : null;
  const candidates = items.filter((item) => {
    if (scopeIds && !scopeIds.has(item.id)) return false;
    if (!isLeaf(item, items)) return false;
    return item.status !== "out_of_scope";
  });

  const weightedTotal = candidates.reduce((total, item) => total + Math.max(0, item.weight), 0);
  const weightedCompleted = candidates.reduce(
    (total, item) => total + Math.max(0, item.weight) * statusDefinitions[item.status].factor,
    0,
  );

  return {
    percent: weightedTotal === 0 ? 0 : Math.round((weightedCompleted / weightedTotal) * 1000) / 10,
    weightedCompleted,
    weightedTotal,
    consideredItems: candidates.length,
  };
}

export function calculatePhaseProgress(items: readonly WorkItem[]): Record<string, number> {
  const phases = items.filter((item) => item.itemType === "phase" && item.parentId === null);
  return Object.fromEntries(
    phases.map((phase) => [phase.id, calculateWeightedProgress(items, phase.id).percent]),
  );
}

export function calculateStatusCounts(
  items: readonly WorkItem[],
): Partial<Record<WorkStatus, number>> {
  return items.reduce<Partial<Record<WorkStatus, number>>>((counts, item) => {
    counts[item.status] = (counts[item.status] ?? 0) + 1;
    return counts;
  }, {});
}

export function leafItems(items: readonly WorkItem[]): WorkItem[] {
  return items.filter((item) => isLeaf(item, items));
}
